import { Popover } from 'bootstrap';

/**
 * Format a number with space grouping: 127000 → "127 000"
 */
function formatPrice( num, currency, vat ) {
	return vat ? num.toString().replace( /\B(?=(\d{3})+(?!\d))/g, ' ' ) + ' ' + currency + ' + ' + vat : num.toString().replace( /\B(?=(\d{3})+(?!\d))/g, ' ' ) + ' ' + currency;
}

/**
 * Initialize a single calculator instance.
 */
function initCalculator( el ) {
	const configEl = el.querySelector( '.kalmes-calc__config' );
	if ( ! configEl ) return;

	const config = JSON.parse( configEl.textContent );
	const { labels } = config;
	const currency = labels.currency || 'EUR';
    const vat = labels.VAT || '';
	const totalSteps = 6;

	// State
	const state = {
		currentStep: 1,
		basePrice: config.basePrice,
		selections: {
			roof: findDefault( config.roofTypes ),
			exterior: findDefault( config.exteriors ),
			interior: findDefault( config.interiors ),
		},
	};

	function findDefault( items ) {
		const idx = items.findIndex( item => item.isDefault );
		return idx >= 0 ? idx : 0;
	}

	// Elements
	const steps = el.querySelectorAll( '.kalmes-calc__step' );
	const progressSteps = el.querySelectorAll( '.kalmes-calc__progress-step' );

	// Initialize popovers
	el.querySelectorAll( '[data-bs-toggle="popover"]' ).forEach( popoverEl => {
		new Popover( popoverEl );
	} );

	/**
	 * Navigate to a step.
	 */
	function goToStep( n ) {
		if ( n < 1 || n > totalSteps ) return;

		state.currentStep = n;

		// Toggle step visibility
		steps.forEach( step => {
			step.classList.toggle( 'is-active', parseInt( step.dataset.step ) === n );
		} );

		// Update progress dots (5 dots for 6 steps; steps 5+6 share dot 5)
		const progressIndex = Math.min( n, 5 );
		progressSteps.forEach( ps => {
			const stepNum = parseInt( ps.dataset.step );
			ps.classList.remove( 'is-active', 'is-completed' );
			if ( stepNum < progressIndex ) {
				ps.classList.add( 'is-completed' );
			} else if ( stepNum === progressIndex ) {
				ps.classList.add( 'is-active' );
			}
		} );

		// Update combo preview image on steps that depend on roof+exterior
		if ( n === 3 || n === 5 ) {
			const comboImage = getPreviewImage( 'exterior', state.selections.exterior );
			if ( comboImage ) {
				const activeStep = el.querySelector( `.kalmes-calc__step[data-step="${ n }"]` );
				const img = activeStep?.querySelector( '.kalmes-calc__preview-img' );
				if ( img ) {
					img.src = comboImage;
				}
			}
		}

		// Build summary on steps 5 and 6
		if ( n >= 5 ) {
			buildSummary();
		}

		// Inject selection data into hidden form fields when entering the form step
		if ( n === 6 ) {
			updateFormHiddenFields();
		}

		updateTotal();
	}

	/**
	 * Resolve the correct preview image for a category, considering
	 * that exterior images depend on the selected roof type.
	 */
	function getPreviewImage( category, index ) {
		if ( category === 'exterior' ) {
			// Exterior images are keyed by roof type index.
			return config.exteriors[ index ]?.previewImages?.[ state.selections.roof ] || '';
		}
		if ( category === 'roof' ) {
			return config.roofTypes[ index ]?.previewImage || '';
		}
		// interior
		return config.interiors[ index ]?.previewImage || '';
	}

	/**
	 * Swap the preview image on a given step with a fade transition.
	 */
	function swapPreviewImage( stepNum, imageUrl ) {
		if ( ! imageUrl ) return;
		const step = el.querySelector( `.kalmes-calc__step[data-step="${ stepNum }"]` );
		const img = step?.querySelector( '.kalmes-calc__preview-img' );
		if ( img ) {
			img.classList.add( 'is-fading' );
			setTimeout( () => {
				img.src = imageUrl;
				img.classList.remove( 'is-fading' );
			}, 300 );
		}
	}

	/**
	 * Select an option within a category.
	 */
	function selectOption( category, index ) {
		state.selections[ category ] = index;

		// Update UI: highlight selected option
		const optionsContainer = el.querySelector( `.kalmes-calc__options[data-category="${ category }"]` );
		if ( optionsContainer ) {
			optionsContainer.querySelectorAll( '.kalmes-calc__option' ).forEach( opt => {
				opt.classList.toggle( 'is-selected', parseInt( opt.dataset.index ) === index );
			} );
		}

		// Swap preview image on current step
		swapPreviewImage( state.currentStep, getPreviewImage( category, index ) );

		updateTotal();
	}

	/**
	 * Calculate and display running total.
	 */
	function updateTotal() {
		const total = getTotal();
		const formatted = formatPrice( total, currency, vat );

		el.querySelectorAll( '.kalmes-calc__total-value' ).forEach( tv => {
			tv.textContent = formatted;
		} );
	}

	function getTotal() {
		const roofPrice = config.roofTypes[ state.selections.roof ]?.price || 0;
		const extPrice = config.exteriors[ state.selections.exterior ]?.price || 0;
		const intPrice = config.interiors[ state.selections.interior ]?.price || 0;
		return state.basePrice + roofPrice + extPrice + intPrice;
	}

	/**
	 * Populate summary tables on steps 5 and 6.
	 */
	function buildSummary() {
		const roof = config.roofTypes[ state.selections.roof ];
		const ext = config.exteriors[ state.selections.exterior ];
		const int = config.interiors[ state.selections.interior ];
		const total = getTotal();

		const summaryData = {
			model: { name: config.projectTitle, price: formatPrice( state.basePrice, currency ) },
			roof: { name: roof?.name || '', price: roof?.price === 0 ? formatPrice( 0, currency ) : ( roof?.priceLabel || formatPrice( roof?.price || 0, currency ) ) },
			exterior: { name: ext?.name || '', price: ext?.price === 0 ? formatPrice( 0, currency ) : ( ext?.priceLabel || formatPrice( ext?.price || 0, currency ) ) },
			interior: { name: int?.name || '', price: int?.price === 0 ? formatPrice( 0, currency ) : ( int?.priceLabel || formatPrice( int?.price || 0, currency ) ) },
		};

		// Look up PDF URL from the selected roof type
		const pdfUrl = config.roofTypes?.[ state.selections.roof ]?.pdfUrl || '';

		// Populate all summary tables (steps 5 and 6 both have one)
		el.querySelectorAll( '.kalmes-calc__summary-table' ).forEach( table => {
			Object.entries( summaryData ).forEach( ( [ key, data ] ) => {
				const row = table.querySelector( `[data-row="${ key }"]` );
				if ( row ) {
					row.querySelector( '.kalmes-calc__summary-name' ).textContent = data.name;
					row.querySelector( '.kalmes-calc__summary-price' ).textContent = data.price;
				}
			} );

			const totalCell = table.querySelector( '.kalmes-calc__summary-total-value' );
			if ( totalCell ) {
				totalCell.textContent = formatPrice( total, currency, vat );
			}

			// Show/hide PDF button
			const pdfBtn = table.querySelector( '.kalmes-calc__btn-pdf' );
			if ( pdfBtn ) {
				if ( pdfUrl ) {
					pdfBtn.href = pdfUrl;
					pdfBtn.style.display = '';
				} else {
					pdfBtn.style.display = 'none';
				}
			}
		} );
	}

	/**
	 * Inject calculator selections into Forminator hidden fields
	 * so they are submitted with the form.
	 */
	function updateFormHiddenFields() {
		const roof = config.roofTypes?.[ state.selections.roof ];
		const ext = config.exteriors?.[ state.selections.exterior ];
		const int = config.interiors?.[ state.selections.interior ];

		const fields = {
			'hidden-2': roof?.pdfUrl || '',
			'hidden-3': roof?.name || '',
			'hidden-4': ext?.name || '',
			'hidden-5': int?.name || '',
		};

		Object.entries( fields ).forEach( ( [ name, value ] ) => {
			// Try both name attribute and id attribute (Forminator may use either)
			const input = el.querySelector( `input[name="${ name }"]` )
				|| el.querySelector( `#forminator-field-${ name }` )
				|| document.querySelector( `input[name="${ name }"]` );
			if ( input ) {
				input.value = value;
				// Trigger change event so Forminator picks up the new value
				input.dispatchEvent( new Event( 'change', { bubbles: true } ) );
			}
		} );
	}

	// Event delegation: option clicks
	el.querySelectorAll( '.kalmes-calc__options' ).forEach( optionsEl => {
		optionsEl.addEventListener( 'click', ( e ) => {
			const option = e.target.closest( '.kalmes-calc__option' );
			if ( ! option ) return;

			// Don't select when clicking info link
			if ( e.target.closest( '.kalmes-calc__info-link' ) ) return;

			const category = optionsEl.dataset.category;
			const index = parseInt( option.dataset.index );
			selectOption( category, index );
		} );
	} );

	// Next/Back buttons
	el.querySelectorAll( '.kalmes-calc__btn-next' ).forEach( btn => {
		btn.addEventListener( 'click', () => {
			if ( state.currentStep < totalSteps ) {
				goToStep( state.currentStep + 1 );
			}
		} );
	} );

	el.querySelectorAll( '.kalmes-calc__btn-back' ).forEach( btn => {
		btn.addEventListener( 'click', () => {
			if ( state.currentStep > 1 ) {
				goToStep( state.currentStep - 1 );
			}
		} );
	} );

	// Progress dot clicks
	progressSteps.forEach( ps => {
		ps.addEventListener( 'click', () => {
			const stepNum = parseInt( ps.dataset.step );
			if ( stepNum <= Math.min( state.currentStep, 5 ) ) {
				goToStep( stepNum );
			}
		} );
	} );

	// Initialize total display
	updateTotal();
}

/**
 * Find and initialize all calculator instances on the page.
 */
export function initCalculators() {
	document.querySelectorAll( '.kalmes-calculator' ).forEach( initCalculator );
}
